// src/components/PinterestPublish.js
// Pinterest-Publishing-Panel: Board-Auswahl, Ziel-Link (mit Auto-UTM),
// "Direkt pinnen" / "In Queue legen" + Artikel-Seed aus dem Blog.
// Dazu: <PinQueue /> — Übersicht der geplanten/veröffentlichten Pins.
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { adminFetch } from '../lib/apiClient';

const colors = { black: '#0A0A0A', white: '#FAFAFA', red: '#C41E3A', gray: '#666666', lightGray: '#E5E5E5', green: '#2E7D32' };

// UTM automatisch anhängen (nur für eigene Domains, nur wenn noch kein utm_ da ist)
export const withUtm = (url) => {
  try {
    const u = new URL(url);
    const own = /(^|\.)sarahiver\.com$|(^|\.)siwedding\.de$/.test(u.hostname);
    if (own && ![...u.searchParams.keys()].some(k => k.startsWith('utm_'))) {
      u.searchParams.set('utm_source', 'pinterest');
      u.searchParams.set('utm_medium', 'social');
    }
    return u.toString();
  } catch {
    return url;
  }
};

const Panel = styled.div`
  background: #fff;
  border: 1px solid ${colors.lightGray};
  border-radius: 8px;
  padding: 1.25rem;
  margin-top: 1rem;
`;

const PanelTitle = styled.h3`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${colors.black};
  margin-bottom: 0.9rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 0.75rem;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;

  label {
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${colors.gray};
  }

  input, select {
    border: 1px solid ${colors.lightGray};
    border-radius: 6px;
    padding: 0.55rem 0.7rem;
    font-size: 0.85rem;
    font-family: inherit;
    background: #fff;
  }
`;

const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 0.4rem;
`;

const Btn = styled.button`
  border: none;
  border-radius: 6px;
  padding: 0.65rem 1.1rem;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  background: ${p => (p.$primary ? colors.red : colors.black)};
  color: #fff;
  opacity: ${p => (p.disabled ? 0.5 : 1)};
`;

const Status = styled.p`
  margin-top: 0.6rem;
  font-size: 0.8rem;
  color: ${p => (p.$err ? colors.red : colors.green)};
`;

const SeedRow = styled.div`
  display: flex;
  gap: 0.6rem;
  align-items: flex-end;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px dashed ${colors.lightGray};

  > div { flex: 1; }
`;

const prettifySlug = (slug) =>
  slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export default function PinterestPublish({ getImageBase64, title, description, onSeedArticle, meta }) {
  const [boards, setBoards] = useState([]);
  const [boardId, setBoardId] = useState('');
  const [link, setLink] = useState('https://www.sarahiver.com/');
  const [scheduledDate, setScheduledDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // {msg, err}
  const [slugs, setSlugs] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState('');
  const [seedBusy, setSeedBusy] = useState(false);

  const loadBoards = useCallback(() => {
    adminFetch('/api/pinterest?action=boards')
      .then(r => r.json())
      .then(d => {
        if (d.boards?.length) {
          setBoards(d.boards);
          setBoardId(prev => prev || d.boards[0].id);
        } else if (d.reconnect) {
          setStatus({ msg: 'Pinterest-Verbindung abgelaufen — bitte oben neu verbinden.', err: true });
        } else if (d.error) {
          setStatus({ msg: `Boards: ${d.error}`, err: true });
        } else {
          setStatus({ msg: 'Keine Boards im Pinterest-Konto gefunden — lege auf Pinterest zuerst eine Pinnwand an.', err: true });
        }
      })
      .catch(() => setStatus({ msg: 'Boards konnten nicht geladen werden (Pinterest verbunden?)', err: true }));
  }, []);

  // Verbindung kann in einem anderen Tab entstanden sein
  useEffect(() => {
    const onConnected = () => loadBoards();
    window.addEventListener('pinterestConnected', onConnected);
    return () => window.removeEventListener('pinterestConnected', onConnected);
  }, [loadBoards]);

  useEffect(() => {
    loadBoards();

    adminFetch('/api/pinterest?action=blog_list')
      .then(r => r.json())
      .then(d => setSlugs(d.slugs || []))
      .catch(() => {});
  }, [loadBoards]);

  const seedFromArticle = async () => {
    if (!selectedSlug) return;
    setSeedBusy(true);
    try {
      const meta = await adminFetch(`/api/pinterest?action=blog_meta&slug=${selectedSlug}`).then(r => r.json());
      setLink(meta.url);
      onSeedArticle?.(meta); // setzt KI-Prompt im Generator
    } catch {
      setStatus({ msg: 'Artikel-Daten konnten nicht geladen werden', err: true });
    }
    setSeedBusy(false);
  };

  const submit = useCallback(async (mode) => {
    if (!boardId) return setStatus({ msg: 'Kein Board gewählt', err: true });
    if (!link) return setStatus({ msg: 'Ziel-Link fehlt', err: true });
    if (!title) return setStatus({ msg: 'Headline fehlt (wird zum Pin-Titel)', err: true });

    setBusy(true);
    setStatus(null);
    try {
      const image_base64 = await getImageBase64();
      const boardName = boards.find(b => b.id === boardId)?.name;
      const body = {
        action: mode === 'now' ? 'publish' : 'queue_add',
        board_id: boardId,
        board_name: boardName,
        title,
        description,
        link: withUtm(link),
        image_base64,
        // Layout/Theme für die Auswertung (Pin-Performance)
        meta: meta || null,
        ...(mode === 'queue' && scheduledDate ? { scheduled_date: scheduledDate } : {}),
      };
      const res = await adminFetch('/api/pinterest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      setStatus({
        msg: mode === 'now'
          ? '✅ Pin veröffentlicht!'
          : `✅ In Queue${scheduledDate ? ` für ${scheduledDate}` : ''} — der Cron pinnt automatisch.`,
        err: false,
      });
      window.dispatchEvent(new CustomEvent('pinQueueChanged'));
    } catch (err) {
      setStatus({ msg: String(err.message || err), err: true });
    }
    setBusy(false);
  }, [boardId, link, title, description, scheduledDate, boards, getImageBase64, meta]);

  return (
    <Panel>
      <PanelTitle>📌 Auf Pinterest veröffentlichen</PanelTitle>

      <SeedRow>
        <Field>
          <label>Pin aus Blog-Artikel erstellen</label>
          <select value={selectedSlug} onChange={e => setSelectedSlug(e.target.value)}>
            <option value="">— Artikel wählen —</option>
            {slugs.map(s => <option key={s} value={s}>{prettifySlug(s)}</option>)}
          </select>
        </Field>
        <Btn onClick={seedFromArticle} disabled={!selectedSlug || seedBusy}>
          {seedBusy ? '…' : 'Übernehmen → KI'}
        </Btn>
      </SeedRow>

      <Row>
        <Field>
          <label>Board</label>
          <select value={boardId} onChange={e => setBoardId(e.target.value)}>
            {boards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </Field>
        <Field>
          <label>Geplant für (leer = nächster Cron-Lauf)</label>
          <input type="date" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)} />
        </Field>
      </Row>
      <Field style={{ marginBottom: '0.75rem' }}>
        <label>Ziel-Link (UTM wird automatisch ergänzt)</label>
        <input type="url" value={link} onChange={e => setLink(e.target.value)} placeholder="https://www.sarahiver.com/blog/..." />
      </Field>

      <Buttons>
        <Btn $primary onClick={() => submit('queue')} disabled={busy}>
          {busy ? '…' : '📥 In Queue legen'}
        </Btn>
        <Btn onClick={() => submit('now')} disabled={busy}>
          {busy ? '…' : '🚀 Direkt pinnen'}
        </Btn>
      </Buttons>

      {status && <Status $err={status.err}>{status.msg}</Status>}
    </Panel>
  );
}

// ==========================================
// QUEUE-ÜBERSICHT
// ==========================================
const QueueWrap = styled.div`
  background: #fff;
  border: 1px solid ${colors.lightGray};
  border-radius: 8px;
  padding: 1.25rem;
  margin-top: 1.5rem;
`;

const QueueTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;

  th {
    text-align: left;
    font-size: 0.65rem;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${colors.gray};
    padding: 0.4rem 0.5rem;
    border-bottom: 1px solid ${colors.lightGray};
  }

  td {
    padding: 0.55rem 0.5rem;
    border-bottom: 1px solid #f0f0f0;
    vertical-align: top;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 99px;
  font-size: 0.65rem;
  font-weight: 700;
  background: ${p => p.$s === 'published' ? '#E8F5E9' : p.$s === 'failed' ? '#FDECEA' : '#FFF8E1'};
  color: ${p => p.$s === 'published' ? colors.green : p.$s === 'failed' ? colors.red : '#8a6d00'};
`;

const SmallBtn = styled.button`
  border: 1px solid ${colors.lightGray};
  background: #fff;
  border-radius: 5px;
  padding: 0.25rem 0.55rem;
  font-size: 0.7rem;
  cursor: pointer;
  margin-right: 0.35rem;

  &:hover { border-color: ${colors.black}; }
`;

// ─────────────────────────────────────────────────────────────────────────────
// VERBINDUNGS-PANEL
// ─────────────────────────────────────────────────────────────────────────────
// Zeigt, ob die Pinterest-App per OAuth verbunden ist, und startet den Flow.
// Ohne gültige Verbindung schlagen Direkt-Pin, Queue und Cron fehl — deshalb
// steht das Panel ganz oben auf der Pinterest-Seite.
export function PinterestConnect() {
  const [state, setState] = useState({ loading: true });
  const [busy, setBusy] = useState(false);

  // Antwort erst als Text lesen: stürzt die Function ab, liefert Vercel HTML —
  // ein blindes r.json() würde daraus nur "Unexpected token 'A'" machen.
  const readJson = async (res) => {
    const text = await res.text();
    try {
      return JSON.parse(text);
    } catch {
      throw new Error(
        `Serverfehler (HTTP ${res.status}). Antwort war kein JSON: ${text.slice(0, 120)}`
      );
    }
  };

  const load = useCallback(() => {
    adminFetch('/api/pinterest?action=status')
      .then(readJson)
      .then(d => setState({ loading: false, ...d }))
      .catch(err => setState({ loading: false, error: String(err.message || err) }));
  }, []);

  const selftest = async () => {
    setBusy(true);
    try {
      const data = await readJson(await adminFetch('/api/pinterest?action=selftest'));
      setState(s => ({ ...s, selftest: data, error: null }));
    } catch (err) {
      setState(s => ({ ...s, error: String(err.message || err) }));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  // Der OAuth-Flow läuft in einem zweiten Tab. Kommt der Fokus zurück, Status
  // neu prüfen — und bei frischer Verbindung die Board-Listen benachrichtigen.
  useEffect(() => {
    const onFocus = () => {
      adminFetch('/api/pinterest?action=status')
        .then(readJson)
        .then(d => {
          setState(s => {
            if (d.connected && !s.connected) {
              window.dispatchEvent(new CustomEvent('pinterestConnected'));
            }
            return { loading: false, ...d };
          });
        })
        .catch(() => {});
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const res = await adminFetch('/api/pinterest?action=oauth_url');
      const data = await readJson(res);
      if (!res.ok) throw new Error(data.error || 'Fehler');
      // Neues Fenster: nach der Zustimmung landet Pinterest auf /api/pinterest
      window.open(data.url, '_blank', 'noopener');
    } catch (err) {
      setState(s => ({ ...s, error: String(err.message || err) }));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Verbindung trennen? Queue und Cron pausieren, bis neu verbunden wird.')) return;
    setBusy(true);
    await adminFetch('/api/pinterest?action=disconnect', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'disconnect' }),
    }).catch(() => {});
    setBusy(false);
    load();
  };

  if (state.loading) {
    return <Panel><PanelTitle>Pinterest-Verbindung</PanelTitle><Status>Prüfe Verbindung…</Status></Panel>;
  }

  return (
    <Panel>
      <PanelTitle>Pinterest-Verbindung</PanelTitle>

      {state.connected ? (
        <>
          <Status>
            Verbunden{state.username ? ` als @${state.username}` : ''}
            {' · '}Token erneuert sich automatisch
            {state.expires_at ? ` (aktuell gültig bis ${new Date(state.expires_at).toLocaleString('de-DE')})` : ''}
          </Status>
          {!state.can_write && (
            <Status style={{ color: colors.red }}>
              Achtung: Die Verbindung hat keine Schreibrechte (pins:write). Direkt-Pin,
              Queue und Cron werden fehlschlagen. Neu verbinden, sobald die App für
              Schreib-Scopes freigegeben ist.
            </Status>
          )}
          {/* Früher stand hier ein fester Hinweis auf den Trial-Zugriff — seit der
              Freigabe des Standardzugriffs veraltet. Jetzt: der tatsächliche
              Cron-Zustand. */}
          <Status style={{ color: state.cron_secret_set === false ? colors.red : colors.gray }}>
            {state.cron_secret_set === false
              ? 'Automatisches Pinnen inaktiv: CRON_SECRET ist in Vercel nicht gesetzt. Variable anlegen und neu deployen.'
              : `Automatisches Pinnen: täglich vormittags, ${state.pins_per_day || 1} Pin${(state.pins_per_day || 1) === 1 ? '' : 's'} pro Tag (PINTEREST_PINS_PER_DAY).`}
          </Status>
          <Buttons>
            <Btn type="button" onClick={connect} disabled={busy}>Neu verbinden</Btn>
            <Btn type="button" onClick={disconnect} disabled={busy}>Trennen</Btn>
          </Buttons>
        </>
      ) : (
        <>
          <Status>
            Nicht verbunden.{state.fallback ? ' Aktuell läuft nur das manuell gesetzte PINTEREST_ACCESS_TOKEN — das verfällt nach 24 Stunden.' : ''}
          </Status>
          <Buttons>
            <Btn type="button" onClick={connect} disabled={busy}>
              {busy ? 'Öffne Pinterest…' : 'Pinterest verbinden'}
            </Btn>
          </Buttons>
        </>
      )}

      {state.error && (
        <>
          <Status style={{ color: colors.red }}>{state.error}</Status>
          <Buttons>
            <Btn type="button" onClick={selftest} disabled={busy}>Selbsttest ausführen</Btn>
          </Buttons>
        </>
      )}

      {state.selftest && (
        <pre style={{
          marginTop: '0.75rem', padding: '0.75rem', background: '#F7F7F7',
          border: `1px solid ${colors.lightGray}`, borderRadius: 6,
          fontSize: '0.72rem', lineHeight: 1.5, overflowX: 'auto',
        }}>
          {JSON.stringify(state.selftest, null, 2)}
        </pre>
      )}
    </Panel>
  );
}

// Letzte Cron-Läufe — ersetzt das kurzlebige Vercel-Log im Hobby-Plan
function CronRuns({ data }) {
  if (!data) return null;
  if (data.missing) {
    return (
      <p style={{ fontSize: '0.72rem', color: colors.gray, margin: '0 0 1rem' }}>
        Cron-Protokoll noch nicht aktiv: <code>scripts/pinterest-cron-runs-migration.sql</code> in Supabase ausführen.
      </p>
    );
  }
  const runs = (data.runs || []).slice(0, 5);
  return (
    <div style={{ margin: '0 0 1.2rem' }}>
      <div style={{ fontSize: '0.62rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: colors.gray, marginBottom: '0.4rem' }}>
        Letzte automatische Läufe
      </div>
      {runs.length === 0 ? (
        <p style={{ fontSize: '0.74rem', color: colors.gray, margin: 0 }}>
          Noch kein Lauf protokolliert. Der nächste kommt am Vormittag.
        </p>
      ) : (
        <QueueTable>
          <tbody>
            {runs.map(run => {
              const failed = (run.results || []).filter(x => !x.ok);
              return (
                <tr key={run.id}>
                  <td style={{ whiteSpace: 'nowrap', width: '9.5rem' }}>
                    {new Date(run.started_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    {run.trigger === 'manuell' && <span style={{ color: colors.gray }}> · manuell</span>}
                  </td>
                  <td style={{ width: '6rem' }}>
                    <StatusBadge $s={!run.ok ? 'failed' : failed.length ? 'failed' : 'published'}>
                      {!run.ok ? 'Fehler' : `${run.published} veröffentlicht`}
                    </StatusBadge>
                  </td>
                  <td style={{ fontSize: '0.7rem', color: run.ok && !failed.length ? colors.gray : colors.red }}>
                    {run.error
                      || (failed.length ? failed.map(f => `${f.title}: ${f.error}`).join(' · ') : null)
                      || (run.published === 0 ? 'Kein Pin fällig' : (run.results || []).map(x => x.title).join(' · '))}
                    {run.metrics?.synced != null && (
                      <span style={{ color: colors.gray }}> · Statistik: {run.metrics.synced} Pins</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </QueueTable>
      )}
    </div>
  );
}

export function PinQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runs, setRuns] = useState(null);

  const load = useCallback(() => {
    adminFetch('/api/pinterest?action=queue_list')
      .then(r => r.json())
      .then(d => setItems(d.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    adminFetch('/api/pinterest?action=cron_runs')
      .then(r => r.json())
      .then(d => setRuns(d))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const onChange = () => load();
    window.addEventListener('pinQueueChanged', onChange);
    return () => window.removeEventListener('pinQueueChanged', onChange);
  }, [load]);

  const act = async (action, id) => {
    await adminFetch('/api/pinterest', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, id }),
    });
    load();
  };

  // Geplanter Pin → zurück in die Entwürfe, dort aufgeklappt zum Bearbeiten.
  // Danach wieder "In Queue"; das Bild wird dabei neu gerendert.
  const edit = async (id) => {
    const res = await adminFetch('/api/pinterest', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'queue_to_draft', id }),
    });
    load();
    if (res.ok) {
      window.dispatchEvent(new CustomEvent('pinDraftsChanged', { detail: { openId: id } }));
    }
  };

  if (loading) return null;

  return (
    <QueueWrap>
      <PanelTitle>🗓 Pin-Queue ({items.filter(i => i.status === 'queued').length} geplant)</PanelTitle>
      <CronRuns data={runs} />
      {items.length === 0 ? (
        <p style={{ fontSize: '0.8rem', color: colors.gray }}>
          Noch keine Pins in der Queue. Oben generieren und "In Queue legen" — der tägliche Cron übernimmt den Rest.
        </p>
      ) : (
        <QueueTable>
          <thead>
            <tr><th>Status</th><th>Titel</th><th>Board</th><th>Geplant</th><th></th></tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.id}>
                <td>
                  <StatusBadge $s={item.status}>
                    {item.status === 'published' ? 'Live' : item.status === 'failed' ? 'Fehler' : 'Geplant'}
                  </StatusBadge>
                  {item.error && <div style={{ color: colors.red, fontSize: '0.65rem', marginTop: 2 }}>{item.error}</div>}
                </td>
                <td>
                  {item.title}
                  <div style={{ color: colors.gray, fontSize: '0.65rem' }}>{item.link}</div>
                </td>
                <td>{item.board_name || '—'}</td>
                <td>{item.scheduled_date || 'nächster Lauf'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {item.status !== 'published' && (
                    <>
                      <SmallBtn onClick={() => edit(item.id)}>Bearbeiten</SmallBtn>
                      {item.status === 'queued' && (
                        <SmallBtn onClick={() => act('queue_publish_now', item.id)}>Jetzt pinnen</SmallBtn>
                      )}
                      <SmallBtn onClick={() => act('queue_delete', item.id)}>Löschen</SmallBtn>
                    </>
                  )}
                  {item.status === 'published' && item.pin_id && (
                    <a
                      href={`https://www.pinterest.com/pin/${item.pin_id}/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: '0.7rem' }}
                    >
                      Pin ansehen →
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </QueueTable>
      )}
    </QueueWrap>
  );
}
